import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBrand } from "@/lib/brand";
import { useSiteContent } from "@/lib/siteContent";
import { ArrowRight, ShieldCheck, Sparkles, Star, Award, Clock, Lock, Users, Zap } from "lucide-react";

function Eyebrow({ children }) {
  return <div className="label-eyebrow">{children}</div>;
}

function TealIcon({ children }) {
  return <div className="teal-icon-circle">{children}</div>;
}

export default function LandingPage() {
  const { content } = useSiteContent();
  const brand = useBrand();
  const { user } = useAuth();

  return (
    <div className="bg-[#05070C] text-[#E8ECF3]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#2DD4BF]/10 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-36 text-center relative fade-up">
          <Eyebrow>{content.hero_eyebrow}</Eyebrow>
          <h1 className="font-display font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight mt-8">
            {content.hero_headline}
            <br />
            <span className="serif-italic text-[#2DD4BF]">{content.hero_headline_italic}</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
            {content.hero_sub}
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={user ? "/dashboard" : "/register"} data-testid="hero-cta-primary">
              <button className="pill-solid h-14 px-10 font-medium text-base">
                {content.hero_cta_primary} <ArrowRight className="inline ml-2 h-4 w-4" />
              </button>
            </Link>
            <Link to="/professionals" data-testid="hero-cta-secondary">
              <button className="pill-glow h-14 px-10 font-medium text-base">
                {content.hero_cta_secondary}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value pillars */}
      <section className="border-y border-[#142542] bg-[#0A1223]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-16">
          <div>
            <Eyebrow>Seasoned Industry Leaders</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-4">
              Expertise <span className="serif-italic text-[#2DD4BF]">Trust</span>
            </h2>
            <p className="text-[#94A3B8] mt-6 text-lg leading-relaxed">
              Every expert on 2nd Innings is a decorated veteran of Indian industry. CFOs, HR heads, engineers, educators, lawyers — with 25+ years of average experience.
            </p>
          </div>
          <div>
            <Eyebrow>Driven by Experience</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-4">
              Purpose <span className="serif-italic text-[#2DD4BF]">Impact</span>
            </h2>
            <p className="text-[#94A3B8] mt-6 text-lg leading-relaxed">
              A single retired veteran can save a founder six months of trial and error. That's the multiplier we're unlocking — one meaningful engagement at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-24">
        <div className="text-center mb-16">
          <Eyebrow>The Process</Eyebrow>
          <h2 className="font-display text-4xl md:text-6xl mt-4">
            {content.process_title || "Elegantly Simple"}
          </h2>
          <div className="w-16 h-[2px] bg-[#2DD4BF] mx-auto mt-6" />
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: <Award className="h-6 w-6" />, t: "Create Your Profile",
              d: "Showcase your decades of experience, expertise, and availability in an elegant portfolio." },
            { icon: <Sparkles className="h-6 w-6" />, t: "Get Discovered",
              d: "Our intelligent matching system connects you with ambitious clients seeking your specific guidance." },
            { icon: <Star className="h-6 w-6" />, t: "Start Earning",
              d: "Conduct meaningful sessions, share your wisdom, and build a rewarding second career." },
          ].map((s, i) => (
            <div key={i} className="section-card text-center">
              <div className="mx-auto mb-6"><TealIcon>{s.icon}</TealIcon></div>
              <div className="font-display text-2xl">{s.t}</div>
              <p className="text-[#94A3B8] mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose */}
      <section className="border-t border-[#142542] bg-[#0A1223]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24">
          <div className="text-center mb-16">
            <Eyebrow>The Advantage</Eyebrow>
            <h2 className="font-display text-4xl md:text-6xl mt-4">{content.why_choose_title || "Why Choose 2nd Innings"}</h2>
            <div className="w-16 h-[2px] bg-[#2DD4BF] mx-auto mt-6" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: <ShieldCheck className="h-6 w-6" />, t: "Verified Professionals",
                d: "Every expert undergoes a rigorous vetting process." },
              { icon: <Star className="h-6 w-6" />, t: "Premium Quality",
                d: "Access top-tier talent with decades of proven success." },
              { icon: <Clock className="h-6 w-6" />, t: "Flexible Scheduling",
                d: "Book sessions that fit perfectly into your timeline." },
              { icon: <Lock className="h-6 w-6" />, t: "Secure Payments",
                d: "Enterprise-grade Razorpay security for all your transactions." },
            ].map((f, i) => (
              <div key={i} className="section-card flex items-start gap-5">
                <TealIcon>{f.icon}</TealIcon>
                <div>
                  <div className="font-display text-xl">{f.t}</div>
                  <p className="text-[#94A3B8] mt-2">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
        <h2 className="font-display text-3xl md:text-5xl leading-tight">
          {content.cta_final_title}
        </h2>
        <p className="text-[#94A3B8] mt-4 max-w-xl mx-auto">{content.cta_final_sub}</p>
        <div className="mt-10">
          <Link to="/register" data-testid="final-cta-signup">
            <button className="pill-glow h-14 px-12 font-medium">Join Now</button>
          </Link>
        </div>
      </section>
    </div>
  );
}
